import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './About.css';

const About = () => {
  // Scroll animation effect
  useEffect(() => {
    const handleScroll = () => {
      const elements = document.querySelectorAll('.scroll-reveal');
      elements.forEach(el => {
        const rect = el.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight - 100;
        if (isVisible) el.classList.add('visible');
      });
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Core values
  const values = [
    {
      title: 'Quality First',
      description: 'We source only the finest materials and partner with premium brands for lasting durability.',
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M20 7L9 18L4 13" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )
    },
    {
      title: 'Expert Care',
      description: 'Our certified optometrists ensure precise prescriptions and perfect frame fitting.',
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="12" cy="8" r="4" />
          <path d="M5.5 20v-2a6.5 6.5 0 0 1 13 0v2" />
        </svg>
      )
    },
    {
      title: 'Customer First',
      description: 'Your satisfaction and eye health are our top priorities in every interaction.',
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      )
    },
    {
      title: 'Innovation',
      description: 'We embrace the latest lens technology and frame designs for better vision.',
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
        </svg>
      )
    }
  ];

  // Why choose us
  const whyChooseUs = [
    { title: 'Premium Quality', description: 'All our products meet international quality standards.' },
    { title: 'Expert Fitting', description: 'Professional optometrists ensure perfect fit and comfort.' },
    { title: 'Wide Selection', description: 'Thousands of frames and lenses for every need.' },
    { title: 'Affordable Prices', description: 'Competitive pricing with regular offers and discounts.' }
  ];

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="container">
          <div className="about-hero-content">
            <span className="hero-badge">WELCOME TO VISION CARE OPTICS</span>
            <h1 className="hero-title">
              <span className="hero-line">Clear Vision,</span>
              <span className="hero-line highlight">Beautifully Crafted</span>
            </h1>
            <p className="hero-description">
              Dedicated to providing premium eyewear and expert eye care services
              to help you see the world with perfect clarity.
            </p>
          </div>
        </div>
        <div className="hero-wave">
          <svg viewBox="0 0 1440 320" preserveAspectRatio="none">
            <path fill="#ffffff" fillOpacity="1" d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,122.7C672,117,768,139,864,154.7C960,171,1056,181,1152,170.7C1248,160,1344,128,1392,112L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
          </svg>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="story-section">
        <div className="container">
          <div className="story-grid">
            <div className="story-image scroll-reveal">
              <div className="image-wrapper">
                <img src="/assets/hero2.jpeg" alt="Our Story - Vision Care Optics" />
                <div className="image-overlay"></div>
                <div className="experience-badge">
                  <span className="years">20+</span>
                  <span className="text">Years of Excellence</span>
                </div>
              </div>
            </div>

            {/* story content with tag on first line and title on second */}
            <div className="story-content scroll-reveal">
              <span className="section-tag story-tag">OUR JOURNEY</span>

              {/* single h2 line (keeps whole title on one line on desktop) */}
              <h2 className="section-title story-title">A Legacy of Excellence</h2>

              <p className="story-text">
                Vision Care Optics began with a simple mission: to provide quality eyewear
                and expert eye care to our community. Over two decades, we've grown from
                a small optical shop to a trusted destination for premium eyewear.
              </p>
              <p className="story-text">
                What sets us apart is our commitment to personalized service. Every customer
                receives individual attention from our experienced optometrists, ensuring
                perfect frame selection and precise lens fitting.
              </p>

              <div className="story-stats">
                <div className="stat-item">
                  <span className="stat-number">20+</span>
                  <span className="stat-label">Years Experience</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">15+</span>
                  <span className="stat-label">Expert Optometrists</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">10k+</span>
                  <span className="stat-label">Happy Customers</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="mission-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">OUR PURPOSE</span>
            <h2 className="section-title">
              <span className="title-line">Mission &</span>
              <span className="title-line highlight">Vision</span>
            </h2>
          </div>

          <div className="mission-grid">
            <div className="mission-card scroll-reveal">
              <div className="mission-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#3498db" strokeWidth="1.5">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 16v-4M12 8h.01" />
                </svg>
              </div>
              <h3>Our Mission</h3>
              <p>To enhance lives through better vision by providing high-quality eyewear and exceptional eye care services.</p>
            </div>

            <div className="mission-card scroll-reveal">
              <div className="mission-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#3498db" strokeWidth="1.5">
                  <path d="M2 12h20M12 2v20M4.93 4.93l14.14 14.14M19.07 4.93L4.93 19.07" />
                </svg>
              </div>
              <h3>Our Vision</h3>
              <p>To be the most trusted name in eye care, known for quality, expertise, and customer satisfaction.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="values-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">WHAT WE BELIEVE IN</span>
            <h2 className="section-title">
              <span className="title-line">Our Core</span>
              <span className="title-line highlight">Values</span>
            </h2>
          </div>

          <div className="values-grid">
            {values.map((value, index) => (
              <div key={index} className="value-card scroll-reveal">
                <div className="value-icon">{value.icon}</div>
                <h3 className="value-title">{value.title}</h3>
                <p className="value-description">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-choose-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">WHY CUSTOMERS LOVE US</span>
            <h2 className="section-title">
              <span className="title-line">Why Choose Vision Care Optics</span>
              <span className="title-line highlight"></span>
            </h2>
          </div>

          <div className="why-grid">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="why-card scroll-reveal">
                <div className="why-number">0{index + 1}</div>
                <h3 className="why-title">{item.title}</h3>
                <p className="why-description">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-cta">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">
              <span className="cta-line">Experience the</span>
              <span className="cta-line highlight">Difference</span>
            </h2>
            <p className="cta-description">
              Visit our store for a personalized consultation or browse our collection online.
            </p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn-primary">
                Contact Us
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </Link>

              <Link to="/products" className="btn-secondary">Shop Now</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;